import { UserTypes } from 'store/auth/types'
import { Navigate } from 'react-router'
import App from 'pages/app/App.tsx'
import Auth from 'pages/auth/Auth.tsx'
import SalesApp from 'pages/sales/SalesApp.tsx'
import Login from 'pages/auth/login/Login'
import Register from 'pages/auth/register/Register'
import ResetPassword from 'pages/auth/reset-password/ResetPassword'
import ProtectedRoute from 'router/protected-route/ProtectedRoute'
import Home from 'pages/sales/home/Home'
import AccountSelection from 'pages/app/account-selection/AccountSelection.tsx'
import CustomerEmail from 'pages/app/customer-email/CustomerEmail'
import Overview from 'pages/app/overview/Overview'
import Producer from 'pages/app/producer/Producer.tsx'
import Billing from 'pages/app/billing/Billing'
import Invoices from 'pages/app/billing/invoices/Invoices'
import EditInstalment from 'pages/app/billing/edit-instalment/EditInstalment.tsx'
import BillingCyclesOverview from 'pages/app/billing/billing-cycles/BillingCyclesOverview.tsx'
import Consumption from 'pages/app/consumption/Consumption'
import User from 'pages/app/user/User'
import Support from 'pages/app/support/Support'
import EventLog from 'pages/app/event-log/EventLog'
import { createBrowserRouter } from 'react-router'
import EnergyOverview from 'pages/app/energy-overview/EnergyOverview'
import MeterReadings from 'pages/app/consumption/meter-readings/MeterReadings'
import Personal from 'pages/app/user/personal/Personal'
import SupportContact from 'pages/app/support/contact/Contact'
import Payment from 'pages/app/user/payment/Payment'
import Faq from 'pages/app/support/faq/Faq'
import Add from 'features/contracts/add/Add'
import ContractsIndex from 'features/contracts/index/Index'
import Terminate from 'features/contracts/terminate/Terminate'
import { isAuthenticated } from 'utils/localStorageService'
import Friends from 'pages/app/referral/invited-friends/InvitedFriends.tsx'
import Addresses from 'pages/app/addresses/Addresses.tsx'
import Contact from 'pages/app/user/contact/Contact.tsx'
import Move from 'pages/app/move/Move.tsx'
import RootLayout from './RootLayout.tsx'
import PageLoading from 'components/page-loading/PageLoading.tsx'
import Details from 'pages/sales/details/Details'
import Events from 'pages/sales/events/Events'
import RouteWithSelectedContract from 'router/route-with-selected-contract/RouteWithSelectedContract.tsx'
import ElectricityConsumption from 'pages/app/consumption/your-consumption/electricity/ElectricityConsumption.tsx'
import GasConsumption from 'pages/app/consumption/your-consumption/gas/GasConsumption.tsx'
import MarketPrices from 'pages/app/market-prices/MarketPrices.tsx'
import ContactForm from 'pages/app/user/contact-form/ContactForm.tsx'
import PaymentForm from 'pages/app/user/payment-form/PaymentForm.tsx'
import EditMeterReadings from 'pages/app/consumption/edit-meter-readings/EditMeterReadings.tsx'
import Referral from 'pages/app/referral/Referral.tsx'
import HowItWorks from 'pages/app/referral/how-it-works/HowItWorks.tsx'
import { routes } from 'types/routes.ts'
import RedirectToIndex from 'pages/RedirectToIndex.tsx'
import BillingCycleDetail from 'pages/app/billing/billing-cycle-detail/BillingCycleDetail.tsx'
import Instalment from 'pages/app/billing/instalment/Instalment.tsx'
import AddressDetail from 'pages/app/addresses/AddressDetail.tsx'
import { Layouts } from 'layouts/types.ts'
import MoreNavigation from 'layouts/components/navigation/mobile-navigation/MoreNavigation.tsx'
import GuaranteeOfOrigin from 'pages/app/guarantee-of-origin'
import GuaranteeOfOriginWhat from 'pages/app/guarantee-of-origin/what/GOWhat.tsx'
import GuaranteeOfOriginStatus from 'pages/app/guarantee-of-origin/status/GOStatus.tsx'
import PersonalForm from 'pages/app/user/personal-form/PersonalForm.tsx'
import LoginDetails from 'pages/app/user/login-details/LoginDetails.tsx'
import LoginDetailsForm from 'pages/app/user/login-details-form/LoginDetailsForm.tsx'
import Tariffs from 'pages/app/tariffs/Tariffs.tsx'
import TariffsOverview from 'pages/app/tariffs/overview/TariffsOverview.tsx'
import TariffsProducts from 'pages/app/tariffs/products/TariffsProducts.tsx'
import TariffsDiscounts from 'pages/app/tariffs/discounts/TariffsDiscounts.tsx'
import SwitchToFix from 'pages/app/switch-to-fix/SwitchToFix.tsx'
import Complaint from 'pages/app/support/complaint/Complaint.tsx'
import ComplaintForm from 'pages/app/support/complaint-form/ComplaintForm.tsx'
import MysterySwitch from 'pages/app/mystery-switch/MysterySwitch.tsx'
import CancelSupplierSwitch from 'pages/app/cancel-supplier-switch/CancelSupplierSwitch.tsx'
import PriceHistory from 'pages/app/consumption/price-history/PriceHistory.tsx'
import Dashboard from 'pages/sales/dashboard/Dashboard.tsx'

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <PageLoading isLoading={false} showError />,
    children: [
      { path: '/', element: <RedirectToIndex replace /> },
      {
        path: '/sales',
        element: (
          <Navigate
            to={
              isAuthenticated().sales
                ? `${routes.SALES_HOME}${document.location.search}`
                : `${routes.LOGIN_SALES}${document.location.search}`
            }
            replace
          />
        )
      },
      {
        path: '/superuser',
        element: <Navigate to={isAuthenticated().superuser ? routes.CUSTOMER_EMAIL : routes.LOGIN_SUPER_USER} replace />
      },

      // AUTH
      {
        element: <Auth />,
        children: [
          // LOGIN ROUTES
          ...[routes.LOGIN, routes.LOGIN_SUPER_USER, routes.LOGIN_SALES].map((path) => ({
            path,
            element: <Login />
          })),

          // REGISTER ROUTE
          { path: routes.REGISTER, element: <Register /> },

          // ACTIVATE / RESET PASSWORD ROUTES
          ...[
            routes.ACTIVATE,
            routes.ACTIVATE_SALES,
            routes.ACTIVATE_SUPER_USER,
            routes.REQUEST_NEW_PASSWORD,
            routes.REQUEST_NEW_PASSWORD_SALES,
            routes.REQUEST_NEW_PASSWORD_SUPER_USER,
            routes.RESET_PASSWORD,
            routes.RESET_PASSWORD_SALES,
            routes.RESET_PASSWORD_SUPER_USER
          ].map((path) => ({ path, element: <ResetPassword /> }))
        ]
      },

      // PROTECTED ROUTES (SALES)
      {
        element: <ProtectedRoute userTypes={[UserTypes.SALES]} />,
        children: [
          {
            element: <SalesApp />,
            errorElement: <PageLoading isLoading={false} showError />,
            children: [
              { path: routes.SALES_HOME, element: <Home /> },
              { path: routes.SALES_DETAILS, element: <Details />, handle: { layout: Layouts.EDIT_LAYOUT } },
              { path: routes.SALES_EVENTS, element: <Events /> },
              { path: routes.SALES_DASHBOARD, element: <Dashboard /> }
            ]
          }
        ]
      },

      {
        element: <ProtectedRoute userTypes={[UserTypes.SUPER_USER]} />,
        errorElement: <PageLoading isLoading={false} showError />,
        children: [
          {
            element: <App />,
            children: [
              { path: routes.CUSTOMER_EMAIL, element: <CustomerEmail /> },
              {
                element: <RouteWithSelectedContract />,
                children: [
                  {
                    path: routes.TARIFFS,
                    element: <Tariffs />,
                    children: [
                      { path: routes.TARIFFS_OVERVIEW, element: <TariffsOverview /> },
                      { path: routes.TARIFFS_PRODUCTS, element: <TariffsProducts /> },
                      { path: routes.TARIFFS_DISCOUNTS, element: <TariffsDiscounts /> }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      },

      // PROTECTED ROUTES (APP, SUPER_USER)
      {
        element: <ProtectedRoute userTypes={[UserTypes.CUSTOMER, UserTypes.SUPER_USER]} />,
        errorElement: <PageLoading isLoading={false} showError />,
        children: [
          {
            element: <App />,
            children: [
              { path: routes.ACCOUNT_SELECTION, element: <AccountSelection /> },
              {
                element: <RouteWithSelectedContract />,
                children: [
                  { path: routes.MORE, element: <MoreNavigation /> },
                  { path: routes.OVERVIEW, element: <Overview /> },
                  { path: routes.PRODUCER, element: <Producer /> },
                  { path: routes.SWITCH_TO_FIX, element: <SwitchToFix />, handle: { layout: Layouts.EDIT_LAYOUT } },
                  {
                    path: routes.CONTRACT,
                    children: [
                      { index: true, element: <Addresses /> },
                      { path: routes.CONTRACT_DETAIL, element: <AddressDetail /> }
                    ]
                  },
                  {
                    path: routes.ADDRESSES,
                    children: [
                      { index: true, element: <Navigate replace to={routes.CONTRACT} /> },
                      { path: routes.ADDRESS_DETAIL, element: <Navigate replace to={routes.CONTRACT} /> }
                    ]
                  },
                  { path: routes.PRODUCER, element: <Producer /> },
                  {
                    path: routes.BILLING,
                    element: <Billing />,
                    children: [
                      { path: routes.BILLING_INVOICES, element: <Invoices /> },
                      { path: routes.BILLING_INSTALMENT, element: <Instalment /> },
                      {
                        path: routes.BILLING_EDIT_INSTALMENT,
                        element: <EditInstalment />,
                        handle: { layout: Layouts.EDIT_LAYOUT }
                      },
                      { path: routes.BILLING_CYCLES, element: <BillingCyclesOverview /> },
                      { path: routes.BILLING_CYCLE_DETAIL, element: <BillingCycleDetail /> }
                    ]
                  },
                  {
                    path: routes.CONSUMPTION,
                    element: <Consumption />,
                    children: [
                      { path: routes.CONSUMPTION_ELECTRICITY, element: <ElectricityConsumption /> },
                      { path: routes.CONSUMPTION_GAS, element: <GasConsumption /> },
                      { path: routes.CONSUMPTION_METER_READINGS, element: <MeterReadings /> },
                      {
                        path: routes.CONSUMPTION_EDIT_METER_READINGS,
                        element: <EditMeterReadings />,
                        handle: { layout: Layouts.EDIT_LAYOUT }
                      },
                      { path: routes.CONSUMPTION_PRICE_HISTORY, element: <PriceHistory /> }
                    ]
                  },
                  { path: routes.MARKET_PRICES, element: <MarketPrices /> },
                  {
                    path: routes.GUARANTEE_OF_ORIGIN,
                    element: <GuaranteeOfOrigin />,
                    children: [
                      { index: true, element: <GuaranteeOfOriginWhat /> },
                      { path: routes.GUARANTEE_OF_ORIGIN_STATUS, element: <GuaranteeOfOriginStatus /> }
                    ]
                  },
                  { path: routes.ENERGY_OVERVIEW, element: <EnergyOverview /> },
                  {
                    path: routes.REFERRAL,
                    element: <Referral />,
                    children: [
                      { path: routes.REFERRAL_FRIENDS, element: <Friends /> },
                      {
                        path: routes.REFERRAL_INFO,
                        element: <HowItWorks />
                      }
                    ]
                  },
                  {
                    path: routes.USER,
                    element: <User />,
                    children: [
                      { path: routes.USER_PERSONAL, element: <Personal />, handle: { layout: Layouts.PAGE_LAYOUT } },
                      { path: routes.USER_CONTACT, element: <Contact />, handle: { layout: Layouts.PAGE_LAYOUT } },
                      { path: routes.USER_PAYMENT, element: <Payment />, handle: { layout: Layouts.PAGE_LAYOUT } },
                      { path: routes.USER_LOGIN_DETAILS, element: <LoginDetails />, handle: { layout: Layouts.PAGE_LAYOUT } },
                      {
                        path: routes.USER_EDIT_PERSONAL,
                        element: <PersonalForm />,
                        handle: { layout: Layouts.EDIT_LAYOUT }
                      },
                      {
                        path: routes.USER_EDIT_CONTACT,
                        element: <ContactForm />,
                        handle: { layout: Layouts.EDIT_LAYOUT }
                      },
                      {
                        path: routes.USER_EDIT_PAYMENT,
                        element: <PaymentForm />,
                        handle: { layout: Layouts.EDIT_LAYOUT }
                      },
                      {
                        element: <ProtectedRoute userTypes={[UserTypes.CUSTOMER]} />,
                        errorElement: <PageLoading isLoading={false} showError />,
                        children: [{ path: routes.USER_LOGIN_DETAILS_EDIT, element: <LoginDetailsForm /> }]
                      }
                    ]
                  },
                  {
                    path: routes.MOVE,
                    element: <Move />
                  },
                  {
                    path: routes.SUPPORT,
                    element: <Support />,
                    children: [
                      { path: routes.SUPPORT_CONTACT, element: <SupportContact /> },
                      { path: routes.SUPPORT_FAQ, element: <Faq /> },
                      { path: routes.SUPPORT_COMPLAINT, element: <Complaint /> },
                      { path: routes.SUPPORT_COMPLAINT_FORM, element: <ComplaintForm />, handle: { layout: Layouts.EDIT_LAYOUT } }
                    ]
                  },
                  { path: routes.EVENT_LOG, element: <EventLog /> },
                  {
                    path: routes.CONTRACTS,
                    children: [
                      { index: true, element: <ContractsIndex /> },
                      { path: routes.CONTRACTS_ADD, element: <Add /> },
                      { path: routes.CONTRACTS_MOVE, element: <Add /> },
                      { path: routes.CONTRACTS_TERMINATE, element: <Terminate /> }
                    ]
                  },
                  { path: routes.CANCEL_SUPPLIER_SWITCH, element: <CancelSupplierSwitch />, handle: { layout: Layouts.EDIT_LAYOUT } },
                  { path: routes.MYSTERY_SWITCH, element: <MysterySwitch />, handle: { layout: Layouts.EDIT_LAYOUT } }
                ]
              }
            ]
          }
        ]
      }
    ]
  },

  // FALLBACK ROUTE
  {
    path: '*',
    element: <RedirectToIndex />
  }
])

export default router
